import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiStream } from "@/lib/api";
import { GraduationCap, Play, RotateCcw, Send, Loader2, Trophy, Star, Target, MessageSquare, User, Bot } from "lucide-react";
import "./Training.css";

type Scenario = "mattress_price" | "furniture_quality" | "delivery_time" | "competitor" | "financing" | "not_ready" | "random";

interface ScenarioInfo {
  label: string;
  desc: string;
  icon: string;
}

const SCENARIOS: Record<Scenario, ScenarioInfo> = {
  mattress_price: { label: "Mattress Price Objection", desc: "Customer thinks the mattress is too expensive", icon: "💰" },
  furniture_quality: { label: "Furniture Quality Doubt", desc: "Customer questions build quality or materials", icon: "🪑" },
  delivery_time: { label: "Delivery Timeline", desc: "Customer frustrated with delivery wait times", icon: "🚚" },
  competitor: { label: "Competitor Comparison", desc: "Customer says they found it cheaper elsewhere", icon: "🏪" },
  financing: { label: "Financing Hesitation", desc: "Customer is unsure about financing options", icon: "💳" },
  not_ready: { label: "Not Ready to Buy", desc: "Customer says they're just browsing", icon: "🤔" },
  random: { label: "Random Scenario", desc: "AI picks a surprise objection scenario", icon: "🎲" },
};

interface ChatMsg {
  role: "customer" | "salesperson" | "system";
  content: string;
}

interface GradeResult {
  letter: string;
  score: number;
  strengths: string[];
  improvements: string[];
  overallFeedback: string;
}

function gradeColor(letter: string) {
  if (letter === "A+" || letter === "A") return "#10B981";
  if (letter === "A-" || letter === "B+") return "#34D399";
  if (letter === "B" || letter === "B-") return "#3B82F6";
  if (letter === "C+" || letter === "C") return "#F59E0B";
  if (letter === "C-" || letter === "D+") return "#F97316";
  return "#EF4444";
}

export default function Training() {
  const { user } = useAuth();
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [turnCount, setTurnCount] = useState(0);
  const [isGrading, setIsGrading] = useState(false);
  const [grade, setGrade] = useState<GradeResult | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [bestGrade, setBestGrade] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef(0);

  useEffect(() => {
    try {
      const stats = JSON.parse(localStorage.getItem("showroom_training_stats") || "{}");
      setTotalSessions(stats.totalSessions || 0);
      setBestGrade(stats.bestGrade || null);
    } catch {}
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startSession = async (sc: Scenario) => {
    const sid = ++sessionIdRef.current;
    setScenario(sc);
    setMessages([]);
    setGrade(null);
    setTurnCount(0);
    setSessionActive(true);

    const systemMsg: ChatMsg = { role: "system", content: `Starting ${SCENARIOS[sc].label} scenario...` };
    setMessages([systemMsg]);

    setIsStreaming(true);
    const customerMsg: ChatMsg = { role: "customer", content: "" };
    setMessages((prev) => [...prev, customerMsg]);

    const chatMessages = [
      { role: "user", content: `START_TRAINING_SCENARIO: ${sc}` },
    ];

    try {
      await apiStream("/training/chat", { messages: chatMessages, scenario: sc, turn: 0 }, (chunk) => {
        if (sessionIdRef.current !== sid) return;
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === "customer") {
            updated[updated.length - 1] = { ...last, content: last.content + chunk };
          }
          return updated;
        });
      }, () => {
        if (sessionIdRef.current === sid) setIsStreaming(false);
      });
    } catch {
      if (sessionIdRef.current === sid) setIsStreaming(false);
    }
  };

  const sendResponse = async () => {
    const text = input.trim();
    if (!text || isSending || isStreaming) return;
    const sid = sessionIdRef.current;

    setIsSending(true);
    setInput("");
    const newTurn = turnCount + 1;
    setTurnCount(newTurn);

    const salespersonMsg: ChatMsg = { role: "salesperson", content: text };
    setMessages((prev) => [...prev, salespersonMsg]);

    if (newTurn >= 4) {
      setIsGrading(true);
      const allMessages = [...messages.filter((m) => m.role !== "system"), salespersonMsg];
      const chatHistory = allMessages.map((m) => ({
        role: m.role === "customer" ? "assistant" : "user",
        content: m.content,
      }));

      let gradeText = "";
      const gradeMsg: ChatMsg = { role: "system", content: "" };
      setMessages((prev) => [...prev, gradeMsg]);

      try {
        await apiStream("/training/grade", { messages: chatHistory, scenario }, (chunk) => {
          if (sessionIdRef.current !== sid) return;
          gradeText += chunk;
          setMessages((prev) => {
            const updated = [...prev];
            if (updated.length > 0) {
              updated[updated.length - 1] = { ...updated[updated.length - 1], content: gradeText };
            }
            return updated;
          });
        }, () => {});
      } catch {}

      if (sessionIdRef.current === sid) {
        try {
          const jsonMatch = gradeText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            setGrade(parsed);
            saveStats(parsed.letter);
          }
        } catch {}
        setSessionActive(false);
        setIsGrading(false);
        setIsSending(false);
      }
      return;
    }

    setIsStreaming(true);
    const customerReply: ChatMsg = { role: "customer", content: "" };
    setMessages((prev) => [...prev, customerReply]);

    const allMessages = [...messages.filter((m) => m.role !== "system"), salespersonMsg];
    const chatHistory = allMessages.map((m) => ({
      role: m.role === "customer" ? "assistant" : "user",
      content: m.content,
    }));

    try {
      await apiStream("/training/chat", { messages: chatHistory, scenario, turn: newTurn }, (chunk) => {
        if (sessionIdRef.current !== sid) return;
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === "customer") {
            updated[updated.length - 1] = { ...last, content: last.content + chunk };
          }
          return updated;
        });
      }, () => {
        if (sessionIdRef.current === sid) {
          setIsStreaming(false);
          setIsSending(false);
        }
      });
    } catch {
      if (sessionIdRef.current === sid) {
        setIsStreaming(false);
        setIsSending(false);
      }
    }
  };

  const saveStats = (letterGrade: string) => {
    const gradeOrder = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D+", "D", "D-", "F"];
    const newTotal = totalSessions + 1;
    setTotalSessions(newTotal);
    let newBest = bestGrade;
    if (!bestGrade || gradeOrder.indexOf(letterGrade) < gradeOrder.indexOf(bestGrade)) {
      newBest = letterGrade;
      setBestGrade(newBest);
    }
    localStorage.setItem("showroom_training_stats", JSON.stringify({
      totalSessions: newTotal,
      bestGrade: newBest,
    }));
  };

  const resetSession = () => {
    sessionIdRef.current++;
    setScenario(null);
    setMessages([]);
    setGrade(null);
    setTurnCount(0);
    setSessionActive(false);
    setIsStreaming(false);
    setIsSending(false);
    setIsGrading(false);
  };

  const turnsRemaining = Math.max(0, 4 - turnCount);

  return (
    <div className="page training-page">
      <div className="training-banner">
        <div className="training-banner-orb training-orb-1" />
        <div className="training-banner-orb training-orb-2" />
        <div className="training-banner-content">
          <div className="training-banner-text">
            <h1><GraduationCap size={28} /> Sales Training</h1>
            <p>Practice handling customer objections with AI-powered role-play scenarios</p>
          </div>
          <div className="training-stats-row">
            <div className="training-stat-badge">
              <Target size={16} />
              <span>{totalSessions} Sessions</span>
            </div>
            {bestGrade && (
              <div className="training-stat-badge training-stat-best">
                <Trophy size={16} />
                <span>Best: {bestGrade}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {!scenario ? (
        <div className="training-scenario-select">
          <h2>Choose a Scenario</h2>
          <p className="training-scenario-subtitle">Select an objection type to practice handling. The AI customer will push back — respond like you would on the sales floor.</p>
          <div className="training-scenario-grid">
            {(Object.entries(SCENARIOS) as [Scenario, ScenarioInfo][]).map(([key, info]) => (
              <button key={key} className="training-scenario-card" onClick={() => startSession(key)}>
                <span className="training-scenario-emoji">{info.icon}</span>
                <strong>{info.label}</strong>
                <span className="training-scenario-desc">{info.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="training-session">
          <div className="training-session-header">
            <div className="training-session-info">
              <span className="training-scenario-tag">
                {SCENARIOS[scenario].icon} {SCENARIOS[scenario].label}
              </span>
              {sessionActive && (
                <span className="training-turns-badge">
                  {turnsRemaining > 0 ? `${turnsRemaining} response${turnsRemaining !== 1 ? "s" : ""} left` : "Final response"}
                </span>
              )}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={resetSession}>
              <RotateCcw size={14} /> New Scenario
            </button>
          </div>

          <div className="training-chat">
            {messages.map((msg, i) => (
              <div key={i} className={`training-msg training-msg-${msg.role}`}>
                {msg.role === "customer" && (
                  <div className="training-msg-avatar training-avatar-customer">
                    <User size={16} />
                  </div>
                )}
                {msg.role === "salesperson" && (
                  <div className="training-msg-avatar training-avatar-salesperson">
                    <MessageSquare size={16} />
                  </div>
                )}
                {msg.role === "system" && (
                  <div className="training-msg-avatar training-avatar-system">
                    <Bot size={16} />
                  </div>
                )}
                <div className="training-msg-content">
                  <span className="training-msg-label">
                    {msg.role === "customer" ? "Customer" : msg.role === "salesperson" ? (user?.name || "You") : "Training AI"}
                  </span>
                  <div className="training-msg-text">{msg.content}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {grade && (
            <div className="training-grade-card">
              <div className="training-grade-header">
                <div className="training-grade-circle" style={{ borderColor: gradeColor(grade.letter), color: gradeColor(grade.letter) }}>
                  <span className="training-grade-letter">{grade.letter}</span>
                  <span className="training-grade-score">{grade.score}/100</span>
                </div>
                <div className="training-grade-title">
                  <h3>Performance Review</h3>
                  <p className="training-grade-feedback">{grade.overallFeedback}</p>
                </div>
              </div>
              <div className="training-grade-details">
                {grade.strengths.length > 0 && (
                  <div className="training-grade-section training-grade-strengths">
                    <h4><Star size={14} /> Strengths</h4>
                    <ul>{grade.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                {grade.improvements.length > 0 && (
                  <div className="training-grade-section training-grade-improvements">
                    <h4><Target size={14} /> Areas for Improvement</h4>
                    <ul>{grade.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
              </div>
              <button className="btn btn-primary" onClick={resetSession} style={{ marginTop: 16 }}>
                <Play size={16} /> Try Another Scenario
              </button>
            </div>
          )}

          {sessionActive && (
            <div className="training-input-bar">
              <input
                type="text"
                placeholder={isStreaming ? "Customer is speaking..." : "Type your sales response..."}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendResponse()}
                disabled={isSending || isStreaming || isGrading}
              />
              <button
                className="training-send-btn"
                onClick={sendResponse}
                disabled={!input.trim() || isSending || isStreaming || isGrading}
              >
                {isSending || isGrading ? <Loader2 size={18} className="spinning" /> : <Send size={18} />}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
